import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Check } from "lucide-react";
import { BRAND } from "../data/content";
import { renderBrandPhrases } from "../components/site/BrandLabel";
import { Seo } from "../components/Seo";

const SECTIONS = [
  {
    heading: "Data We Collect",
    blocks: [
      { type: "p", text: "WhatThe? Scan collects no personal data. We do not collect:" },
      {
        type: "list",
        items: [
          "names or contact details",
          "accounts or logins",
          "analytics or tracking data",
          "advertising identifiers",
          "location information",
        ],
      },
      { type: "p", text: "Your data stays on your device except for label photos sent securely for analysis." },
    ],
  },
  {
    heading: "How Scans Are Processed",
    blocks: [
      {
        type: "p",
        text: "WhatThe? Scan uses a privacy-first analysis flow: When you scan a product, the label photo is securely sent to our backend server, which forwards it to a trusted AI service (Gemini) for analysis. This step allows accurate reading of ingredient labels across food and body care products. Images are encrypted in transit, are processed only to generate your result, and are deleted immediately after analysis — they are never used for training or advertising.",
      },
    ],
  },
  {
    heading: "Australian Consumer Focus",
    blocks: [
      {
        type: "p",
        text: "WhatThe? Scan is optimised for the Australian market. Ingredient analysis, allergen flags, and product assessments are tailored for Australian food and body care products and labelling standards. Our data handling complies with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). Because no personal information is collected or stored, your data remains protected from domestic or international breaches.",
      },
    ],
  },
  {
    heading: "OS-Level Diagnostics (Crash Reporting)",
    blocks: [
      {
        type: "p",
        text: "WhatThe? Scan does not include third-party analytics or tracking SDKs. If the app crashes, your device's operating system (Apple iOS) may ask if you'd like to share diagnostic logs. These reports:",
      },
      {
        type: "list",
        items: [
          "are managed entirely by Apple",
          "are anonymised and stripped of user data",
          "contain only technical code logs to help fix bugs",
        ],
      },
    ],
  },
  {
    heading: "No Accounts, No Tracking, No Subscriptions",
    blocks: [
      { type: "p", text: "WhatThe? Scan does not require:" },
      {
        type: "list",
        items: [
          "account creation",
          "login",
          "cloud sync",
          "subscription plans",
          "analytics SDKs",
          "advertising SDKs",
        ],
      },
      {
        type: "p",
        text: "WhatThe? Scan is a one-time purchase — there are no subscriptions, no recurring fees, and no data selling. Only label photos are briefly transmitted for secure analysis; all other data remains on your device.",
      },
    ],
  },
  {
    heading: "Your Device, Your Data",
    blocks: [
      {
        type: "p",
        text: "All scans, history, and settings remain stored locally on your device. Deleting the app permanently removes all data. We do not store or retain your data.",
      },
    ],
  },
  {
    heading: "Children's Privacy",
    blocks: [
      {
        type: "p",
        text: "WhatThe? Scan does not collect any data from users of any age. It is safe for families and children because no personal information is ever collected or stored.",
      },
    ],
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App relative min-h-screen bg-page" data-testid="privacy-policy-page">
      <Seo
        title="Privacy Policy — WhatThe? Scan"
        description="WhatThe? Scan collects no personal data — no accounts, no tracking and no advertising identifiers. Your scans and history stay on your device."
        path="/privacy-policy"
      />
      <header className="sticky top-0 z-50 border-b border-line bg-page/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1240px] items-center justify-between gap-4 px-5 py-3.5 md:px-8">
          <Link to="/" data-testid="privacy-nav-logo" className="flex items-center gap-2.5">
            <img src={BRAND.logo} alt="WhatThe?" className="h-9 w-9 rounded-xl" />
            <span className="text-lg font-bold tracking-tight text-ink">
              WhatThe<span className="text-food-accent">?</span> Scan
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate transition-colors hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.9} />
            Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-24 pt-14 md:px-8 md:pt-20">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-food-accent/25 bg-food-bg px-3.5 py-1.5 text-xs font-semibold text-food-text">
          <ShieldCheck className="h-3.5 w-3.5 text-food-accent" strokeWidth={2} />
          Legal
        </div>

        <h1 className="text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl">
          Privacy Policy
        </h1>
        <p className="mt-3 text-sm text-slate">Last updated: 28 August 2026</p>

        <p className="mt-6 text-base leading-relaxed text-slate md:text-lg">
          {renderBrandPhrases(
            "WhatThe? Scan is designed to be simple, private, and transparent. We do not sell, share, or use your data for advertising. Your scans are processed securely to deliver accurate results."
          )}
        </p>

        <div className="mt-12 space-y-12">
          {SECTIONS.map((section) => (
            <section key={section.heading} data-testid={`privacy-section-${section.heading.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
              <h2 className="text-xl font-bold tracking-tight text-ink md:text-2xl">
                {section.heading}
              </h2>
              <div className="mt-4 space-y-4">
                {section.blocks.map((block, i) =>
                  block.type === "p" ? (
                    <p key={i} className="text-[15px] leading-relaxed text-slate md:text-base">
                      {renderBrandPhrases(block.text)}
                    </p>
                  ) : (
                    <ul key={i} className="space-y-2.5">
                      {block.items.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-food-bg">
                            <Check className="h-3 w-3 text-food-accent" strokeWidth={2.5} />
                          </span>
                          <span className="text-[15px] leading-relaxed text-slate md:text-base">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )
                )}
              </div>
            </section>
          ))}

          <section data-testid="privacy-section-contact">
            <h2 className="text-xl font-bold tracking-tight text-ink md:text-2xl">Contact</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-slate md:text-base">
              If you have any questions, you can reach the developer at:{" "}
              <a
                href="mailto:info@whatthescan.app"
                data-testid="privacy-contact-email"
                className="font-semibold text-food-accent transition-colors hover:text-food-text"
              >
                info@whatthescan.app
              </a>
            </p>
          </section>
        </div>

        <div className="mt-16 border-t border-line pt-8">
          <Link
            to="/"
            data-testid="privacy-footer-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink transition-colors hover:text-food-accent"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.9} />
            Back to home
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium">
            <Link
              to="/privacy-policy"
              data-testid="privacy-crosslink-privacy"
              className="text-slate transition-colors hover:text-food-accent"
            >
              Privacy Policy
            </Link>
            <span className="text-line">·</span>
            <Link
              to="/terms"
              data-testid="privacy-crosslink-terms"
              className="text-slate transition-colors hover:text-food-accent"
            >
              Terms of Service
            </Link>
          </div>
          <p className="mt-6 text-xs text-slate">
            © {new Date().getFullYear()} WhatThe? Ingredient Scanner · Made in Australia
          </p>
        </div>
      </main>
    </div>
  );
}
